import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { activeInstituteTypesQueryOptions } from '@/features/instituteTypes/api/instituteTypes.api';
import { useAmZmFilterOptions } from '@/hooks/useAmZmFilterOptions';
import { useDistrictThanaFilterOptions } from '@/hooks/useDistrictThanaFilterOptions';
import { withOptionCounts } from '@/utils/filters';
import { instituteSubmissionCountsBySubmitterQueryOptions } from '../api/instituteSubmissions.api';
import type { InstituteSubmissionCountsBySubmitterParams } from '../types/instituteSubmission.types';
import { getInstituteSubmissionFilterFields } from '../utils/getInstituteSubmissionFilterFields';

interface UseInstituteSubmissionFilterFieldsParams {
  enabled: boolean;
  amUserId?: number | undefined;
  districtId?: number | undefined;
  countsBySubmitterParams: InstituteSubmissionCountsBySubmitterParams;
}

export function useInstituteSubmissionFilterFields({
  enabled,
  amUserId,
  districtId,
  countsBySubmitterParams,
}: UseInstituteSubmissionFilterFieldsParams) {
  const {
    amOptions,
    zmOptions,
    hasOptionsError: hasHierarchyOptionsError,
  } = useAmZmFilterOptions({ isEnabled: enabled, amUserId });
  const {
    districts,
    thanas,
    hasOptionsError: hasGeoOptionsError,
  } = useDistrictThanaFilterOptions({ isEnabled: enabled, districtId });
  const { data: instituteTypes, isError: instituteTypesFailed } = useQuery({
    ...activeInstituteTypesQueryOptions(),
    enabled,
  });
  const { data: countsBySubmitter, isError: hasCountsError } = useQuery({
    ...instituteSubmissionCountsBySubmitterQueryOptions(countsBySubmitterParams),
    enabled,
  });

  const fields = useMemo(
    () =>
      getInstituteSubmissionFilterFields({
        hasDistrict: districtId !== undefined,
        instituteTypes,
        districts,
        thanas,
        amOptions: withOptionCounts(amOptions, countsBySubmitter?.amCounts),
        zmOptions: withOptionCounts(zmOptions, countsBySubmitter?.zmCounts),
      }),
    [districtId, instituteTypes, districts, thanas, amOptions, zmOptions, countsBySubmitter],
  );

  return {
    fields,
    optionsFailed:
      hasHierarchyOptionsError || hasGeoOptionsError || instituteTypesFailed || hasCountsError,
  };
}
